<script setup>
/**
 * MobileSubcategoryBar
 * Two-level horizontal scrollable navigation for mobile/tablet:
 *   Top strip  – Department chips (bolder, larger)
 *   Bottom strip – Subcategory chips (current style)
 * Shown below mobile-view-controls when departments are loaded.
 */
import { computed, ref, watch, nextTick } from 'vue'

const props = defineProps({
  selectedDepartmentId: { type: String, default: null },
  selectedSubDepartmentId: { type: String, default: null },
  subDepartments: { type: Array, default: () => [] },
  departments: { type: Array, default: () => [] },
  promoDepartmentId: { type: String, default: '__promotions__' },
  subsLoading: { type: Boolean, default: false },
})

const emit = defineEmits(['select-subdepartment', 'select-department'])

// Refs for scroll containers & active chip elements
const deptScrollRef = ref(null)
const activeDeptChipEl = ref(null)
const scrollContainerRef = ref(null)
const activeChipEl = ref(null)

// Show department strip when departments exist and one is selected
const hasDepartments = computed(() => {
  return props.selectedDepartmentId !== null && props.departments.length > 0
})

// Show subcategory strip only for non-promo departments with loaded subs
const hasSubcategories = computed(() => {
  return (
    props.selectedDepartmentId !== null &&
    props.selectedDepartmentId !== props.promoDepartmentId &&
    props.subDepartments.length > 0 &&
    !props.subsLoading
  )
})

// Current department name (for emitting with subdepartment selection)
const currentDepartmentName = computed(() => {
  const dept = props.departments.find((d) => d.id === props.selectedDepartmentId)
  return dept?.name || ''
})

function handleSelectDepartment(dept) {
  emit('select-department', { id: dept.id, name: dept.name })
}

function handleSelectSubdepartment(sub) {
  emit('select-subdepartment', {
    id: sub.id,
    name: sub.name,
    departmentId: props.selectedDepartmentId,
    departmentName: currentDepartmentName.value,
  })
}

// Auto-scroll active department chip into view
watch(
  () => props.selectedDepartmentId,
  async () => {
    await nextTick()
    if (activeDeptChipEl.value && deptScrollRef.value) {
      activeDeptChipEl.value.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  }
)

// Auto-scroll active subcategory chip into view
watch(
  () => props.selectedSubDepartmentId,
  async () => {
    await nextTick()
    if (activeChipEl.value && scrollContainerRef.value) {
      activeChipEl.value.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  }
)

// Reset subcategory scroll when department changes
watch(
  () => props.selectedDepartmentId,
  () => {
    activeChipEl.value = null
    if (scrollContainerRef.value) {
      scrollContainerRef.value.scrollTo({ left: 0, behavior: 'instant' })
    }
  }
)
</script>

<template>
  <div v-if="hasDepartments" class="mobile-subcategory-bar mobile-only">
    <!-- Top: Department chips -->
    <div ref="deptScrollRef" class="departments-scroll">
      <button
        v-for="dept in departments"
        :key="dept.id"
        class="department-chip"
        :class="{
          active: dept.id === selectedDepartmentId,
          promo: dept.id === promoDepartmentId && dept.id !== selectedDepartmentId,
        }"
        :ref="
          (el) => {
            if (dept.id === selectedDepartmentId) activeDeptChipEl = el
          }
        "
        @click="handleSelectDepartment(dept)"
      >
        {{ dept.name }}
      </button>
    </div>

    <!-- Bottom: Subcategory chips (placeholder preserves height) -->
    <div class="subcategory-placeholder">
      <div v-if="hasSubcategories" ref="scrollContainerRef" class="subcategory-scroll">
        <button
          v-for="sub in subDepartments"
          :key="sub.id"
          class="subcategory-chip"
          :class="{ active: sub.id === selectedSubDepartmentId }"
          :ref="
            (el) => {
              if (sub.id === selectedSubDepartmentId) activeChipEl = el
            }
          "
          @click="handleSelectSubdepartment(sub)"
        >
          {{ sub.name }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mobile-subcategory-bar {
  display: none;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  background: var(--bg-primary);
  animation: slideDown 0.2s ease-out;
}

/* ── Department strip (top) ── */
.departments-scroll {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  padding: 4px 0;
  direction: rtl;
}

/* Hide scrollbar */
.departments-scroll::-webkit-scrollbar {
  display: none;
}
.departments-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.department-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 12px;
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    transform 0.15s ease;
  user-select: none;
}

.department-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.department-chip:active {
  transform: scale(0.97);
}

.department-chip.active {
  background: var(--gradient-header);
  border-color: transparent;
  color: white;
  font-weight: var(--font-weight-bold);
}

.department-chip.promo {
  background: linear-gradient(135deg, #fff1f2, #ffe4e6);
  border-color: #fecdd3;
  color: var(--color-error);
}

/* ── Subcategory strip (bottom) ── */
.subcategory-placeholder {
  height: 26px;
  overflow: hidden;
}

.subcategory-scroll {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  padding: 2px 0 4px;
  direction: rtl;
}

/* Hide scrollbar */
.subcategory-scroll::-webkit-scrollbar {
  display: none;
}
.subcategory-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.subcategory-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 3px 8px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 11px;
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    transform 0.15s ease;
  user-select: none;
}

.subcategory-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.subcategory-chip:active {
  transform: scale(0.97);
}

.subcategory-chip.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
  font-weight: var(--font-weight-semibold);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Tablet: 768px - 1279px */
@media (min-width: 768px) and (max-width: 1279px) {
  .mobile-subcategory-bar {
    display: flex;
  }
}

/* Mobile: < 768px */
@media (max-width: 767px) {
  .mobile-subcategory-bar {
    display: flex;
  }

  .subcategory-scroll {
    padding: 2px 0 4px;
  }
}
</style>
